import { ArrowLeftIcon, HelpCircleIcon, LoaderIcon, MinusIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import { useThreadRuntime } from "@assistant-ui/react";

import type { FinancingEligibilityResult } from "@/lib/api";
import { useT } from "@/locale-context";
import { cn } from "@/lib/utils";

/**
 * Renders the financing eligibility estimator.
 *
 * Two-phase tool (see FinancingEligibilityAgent.cs):
 *   1. `status: "awaiting-answers"` — wizard mode. Customer answers 7
 *      qualification questions client-side; final Next fires
 *      `→ Financing answers: {…}` via runtime.append, which the agent
 *      parses into a second call with real values.
 *   2. `status: "ok"` — result mode. Shows qualify-up-to hero +
 *      three payment-option cards.
 */
export function FinancingEligibilityCard({
  data,
}: {
  data: FinancingEligibilityResult;
}) {
  if (data.status === "ok") {
    return <ResultPhase data={data} />;
  }
  return <WizardPhase />;
}

// ── Wizard phase ─────────────────────────────────────────────────

const TOTAL_QUESTIONS = 7;

type IncomeBand = "1500-2500" | "2500-3500" | "3500-5000" | "5500-8500";

type Answers = {
  yearsAtAddress: number;
  housingUnderOneThird?: boolean;
  employedOver12Months?: boolean;
  savesMonthly?: boolean;
  hadCollections?: boolean;
  incomeBand?: IncomeBand;
  dependents: number;
};

function WizardPhase() {
  const t = useT();
  const runtime = useThreadRuntime({ optional: true });
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({
    yearsAtAddress: 5,
    dependents: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const percent = Math.round(((step + 1) / TOTAL_QUESTIONS) * 100);
  const isLastStep = step === TOTAL_QUESTIONS - 1;

  const currentAnswered = isAnswered(step, answers);

  const handleNext = async () => {
    if (!currentAnswered || submitting || submitted) return;
    if (!isLastStep) {
      setStep((s) => s + 1);
      return;
    }
    if (!runtime) return;
    setSubmitting(true);
    try {
      const payload = {
        yearsAtAddress: answers.yearsAtAddress,
        housingUnderOneThird: answers.housingUnderOneThird,
        employedOver12Months: answers.employedOver12Months,
        savesMonthly: answers.savesMonthly,
        hadCollections: answers.hadCollections,
        incomeBand: answers.incomeBand,
        dependents: answers.dependents,
      };
      await runtime.append({
        role: "user",
        content: [
          {
            type: "text",
            text: `\u2192 Financing answers: ${JSON.stringify(payload)}`,
          },
        ],
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardShell
      step={step}
      percent={percent}
      questionKey={step + 1}
      canGoBack={step > 0 && !submitting && !submitted}
      canGoNext={currentAnswered}
      pending={submitting}
      onBack={() => setStep((s) => Math.max(0, s - 1))}
      onNext={handleNext}
    >
      {step === 0 ? (
        <>
          <NumberStepper
            value={answers.yearsAtAddress}
            min={0}
            max={60}
            onChange={(n) => setAnswers((a) => ({ ...a, yearsAtAddress: n }))}
          />
          <p className="text-center text-[14px] text-[#353a47]/50 leading-[1.3]">
            {t("financing.q1.hint")}
          </p>
        </>
      ) : null}

      {step >= 1 && step <= 4 ? (
        <YesNoPills
          value={yesNoValueFor(step, answers)}
          onChange={(v) =>
            setAnswers((a) => ({ ...a, ...yesNoPatch(step, v) }))
          }
          yesLabel={t("financing.yes")}
          noLabel={t("financing.no")}
        />
      ) : null}

      {step === 5 ? (
        <IncomeBandGrid
          value={answers.incomeBand}
          onChange={(band) => setAnswers((a) => ({ ...a, incomeBand: band }))}
          labels={{
            "1500-2500": t("financing.incomeBand.1500-2500"),
            "2500-3500": t("financing.incomeBand.2500-3500"),
            "3500-5000": t("financing.incomeBand.3500-5000"),
            "5500-8500": t("financing.incomeBand.5500-8500"),
          }}
        />
      ) : null}

      {step === 6 ? (
        <>
          <NumberStepper
            value={answers.dependents}
            min={0}
            max={10}
            onChange={(n) => setAnswers((a) => ({ ...a, dependents: n }))}
          />
          <p className="text-center text-[14px] text-[#353a47]/50 leading-[1.3]">
            {t("financing.q7.hint")}
          </p>
        </>
      ) : null}
    </WizardShell>
  );
}

function isAnswered(step: number, a: Answers): boolean {
  switch (step) {
    case 0: return typeof a.yearsAtAddress === "number";
    case 1: return a.housingUnderOneThird !== undefined;
    case 2: return a.employedOver12Months !== undefined;
    case 3: return a.savesMonthly !== undefined;
    case 4: return a.hadCollections !== undefined;
    case 5: return !!a.incomeBand;
    case 6: return typeof a.dependents === "number";
    default: return false;
  }
}

function yesNoValueFor(step: number, a: Answers): boolean | undefined {
  switch (step) {
    case 1: return a.housingUnderOneThird;
    case 2: return a.employedOver12Months;
    case 3: return a.savesMonthly;
    case 4: return a.hadCollections;
    default: return undefined;
  }
}

function yesNoPatch(step: number, v: boolean): Partial<Answers> {
  switch (step) {
    case 1: return { housingUnderOneThird: v };
    case 2: return { employedOver12Months: v };
    case 3: return { savesMonthly: v };
    case 4: return { hadCollections: v };
    default: return {};
  }
}

// ── Shared chrome ────────────────────────────────────────────────

function WizardShell({
  step,
  percent,
  questionKey,
  canGoBack,
  canGoNext,
  pending,
  onBack,
  onNext,
  children,
}: {
  step: number;
  percent: number;
  questionKey: number;
  canGoBack: boolean;
  canGoNext: boolean;
  pending: boolean;
  onBack: () => void;
  onNext: () => void;
  children: React.ReactNode;
}) {
  const t = useT();
  const questionText = t(
    `financing.q${questionKey}` as
      | "financing.q1"
      | "financing.q2"
      | "financing.q3"
      | "financing.q4"
      | "financing.q5"
      | "financing.q6"
      | "financing.q7",
  );

  return (
    <div className="my-3 flex flex-col gap-4">
      {/* label */}
      <p className="text-[14px] text-[#353a47] leading-[1.3]">
        {t("financing.label")}
      </p>

      {/* progress row */}
      <div>
        <div className="mb-1.5 flex items-baseline justify-between gap-2 text-[14px] leading-[1.3] text-[#353a47]">
          <span>{t("financing.progress", { n: step + 1, total: TOTAL_QUESTIONS })}</span>
          <span>{percent}%</span>
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-[#d9d9d9]">
          <div
            className="h-full rounded-full bg-[#00c0e8] transition-[width]"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {/* question row */}
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#e7f3ff] to-[#daf5fb]">
          <HelpCircleIcon className="size-4 text-[#00c0e8]" />
        </div>
        <p className="pt-1 font-bold text-[16px] text-[#353a47] leading-[1.3]">
          {questionText}
        </p>
      </div>

      {/* input slot */}
      <div className="flex flex-col gap-2">{children}</div>

      {/* nav row: back arrow + next */}
      <div className="flex items-center gap-3">
        {canGoBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label={t("financing.back")}
            className="flex size-10 shrink-0 items-center justify-center rounded-full text-[#353a47] transition hover:bg-[#e7f3ff]"
          >
            <ArrowLeftIcon className="size-5" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={onNext}
          disabled={!canGoNext || pending}
          translate="no"
          style={{
            backgroundColor: canGoNext && !pending ? "#00c0e8" : "#a8e0f0",
            color: "#ffffff",
          }}
          className={cn(
            "inline-flex h-[44px] flex-1 items-center justify-center gap-2 rounded-full px-4 font-medium text-[16px] transition",
            canGoNext && !pending
              ? "cursor-pointer hover:brightness-95"
              : "cursor-not-allowed",
          )}
        >
          <span
            aria-hidden={!pending}
            className={cn(
              "inline-flex items-center",
              pending ? "opacity-100" : "w-0 opacity-0",
            )}
          >
            <LoaderIcon className="size-4 animate-spin" />
          </span>
          <span>{t("financing.next")}</span>
        </button>
      </div>
    </div>
  );
}

// ── Input subcomponents ─────────────────────────────────────────

function NumberStepper({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  return (
    <div className="flex h-[58px] items-center justify-between rounded-[16px] border border-[#d3dfe7] bg-white px-4">
      <button
        type="button"
        onClick={dec}
        disabled={value <= min}
        aria-label="decrement"
        className="flex size-8 items-center justify-center rounded-full text-[#353a47] transition hover:bg-[#e7f3ff] disabled:opacity-30"
      >
        <MinusIcon className="size-5" />
      </button>
      <span className="font-bold text-[16px] text-[#353a47] leading-[1.3]" translate="no">
        {value}
      </span>
      <button
        type="button"
        onClick={inc}
        disabled={value >= max}
        aria-label="increment"
        className="flex size-8 items-center justify-center rounded-full text-[#353a47] transition hover:bg-[#e7f3ff] disabled:opacity-30"
      >
        <PlusIcon className="size-5" />
      </button>
    </div>
  );
}

function YesNoPills({
  value,
  onChange,
  yesLabel,
  noLabel,
}: {
  value: boolean | undefined;
  onChange: (v: boolean) => void;
  yesLabel: string;
  noLabel: string;
}) {
  return (
    <div className="flex gap-3">
      <PillButton selected={value === true} onClick={() => onChange(true)} tall>
        {yesLabel}
      </PillButton>
      <PillButton selected={value === false} onClick={() => onChange(false)} tall>
        {noLabel}
      </PillButton>
    </div>
  );
}

function IncomeBandGrid({
  value,
  onChange,
  labels,
}: {
  value: IncomeBand | undefined;
  onChange: (b: IncomeBand) => void;
  labels: Record<IncomeBand, string>;
}) {
  const bands: IncomeBand[] = ["1500-2500", "2500-3500", "3500-5000", "5500-8500"];
  return (
    <div className="grid grid-cols-2 gap-3">
      {bands.map((band) => (
        <PillButton
          key={band}
          selected={value === band}
          onClick={() => onChange(band)}
        >
          {labels[band]}
        </PillButton>
      ))}
    </div>
  );
}

function PillButton({
  selected,
  onClick,
  tall,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  tall?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        "flex flex-1 items-center justify-center border px-[22px] py-[16px] text-[16px] font-bold leading-[1.3] text-[#353a47] transition",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00c0e8]",
        tall ? "h-[81px]" : "h-[44px]",
        selected
          ? "rounded-[12px] border-[#00c0e8] bg-[#e7f3ff]"
          : "rounded-[16px] border-[#d3dfe7] bg-white hover:border-[#00c0e8]",
      )}
    >
      {children}
    </button>
  );
}

// ── Result phase ─────────────────────────────────────────────────

function ResultPhase({ data }: { data: FinancingEligibilityResult }) {
  const t = useT();
  const formatter = new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

  return (
    <div className="my-3 flex flex-col gap-4">
      {/* label */}
      <p className="text-[14px] text-[#353a47] leading-[1.3]">
        {t("financing.label")}
      </p>

      {/* Hero */}
      <div className="rounded-[12px] border border-[#d3dfe7] bg-[#daf5fb] px-5 py-4 text-center">
        <p className="font-bold text-[16px] text-[#353a47] leading-[1.3]">
          {t("financing.hero")}
        </p>
        <p className="my-2 font-bold text-[40px] text-[#00c0e8] leading-[1.1]">
          {data.maxVehiclePriceEur != null
            ? formatter.format(data.maxVehiclePriceEur)
            : "—"}
        </p>
        <p className="text-[14px] text-[#353a47] leading-[1.3]">
          {t("financing.hero.sub")}
        </p>
      </div>

      {/* Options label */}
      <p className="text-[14px] text-[#353a47] leading-[1.3]">
        {t("financing.options.label")}
      </p>

      {/* Option cards */}
      {data.options && data.options.length > 0 ? (
        <div className="grid grid-cols-3 gap-3">
          {data.options.map((opt) => (
            <div
              key={opt.termMonths}
              className="flex flex-col items-center gap-1.5 rounded-[16px] border border-[#d3dfe7] bg-white/80 px-2 py-3 shadow-sm"
            >
              <p className="text-[14px] text-[#353a47] leading-[1.3]">
                {opt.termMonths} {t("financing.monthsSuffix")}
              </p>
              <p className="leading-[1.3]">
                <span
                  className="font-bold text-[24px] text-[#00c0e8]"
                  translate="no"
                >
                  {formatter.format(opt.monthlyPaymentEur)}
                </span>
                <span className="ml-1 text-[14px] text-[#353a47]/50">
                  {t("financing.perMonth")}
                </span>
              </p>
              {opt.isPopular ? (
                <span className="inline-flex h-[22px] items-center rounded-full border border-[#00c0e8] px-3 text-[11px] text-[#00c0e8]">
                  {t("financing.optionPopular")}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* Disclaimer */}
      {data.disclaimerNote || t("financing.disclaimer") ? (
        <p className="text-[12px] text-[#353a47]/40 leading-[1.3]">
          {data.disclaimerNote ?? t("financing.disclaimer")}
        </p>
      ) : null}
    </div>
  );
}
